import { z } from 'zod';
import { Id, IsoDateTime } from './common.js';

export const CommandType = z.enum(['ring', 'lost_mode_on', 'lost_mode_off', 'locate_now']);
export type CommandType = z.infer<typeof CommandType>;

/**
 * pending: waiting for the device to connect. delivered: the device has it.
 * done / failed: the device's answer. expired: it never collected it in time.
 */
export const CommandStatus = z.enum(['pending', 'delivered', 'done', 'failed', 'expired']);
export type CommandStatus = z.infer<typeof CommandStatus>;

/** The capability a device must report before each command can be sent to it. */
export const REQUIRED_CAPABILITY: Record<CommandType, string> = {
  ring: 'ring',
  locate_now: 'locate_now',
  lost_mode_on: 'lost_mode',
  lost_mode_off: 'lost_mode',
};

/** Ring and Locate now are only useful soon: a phone that was offline for a day shouldn't start ringing. */
export const COMMAND_TTL_MS = 10 * 60 * 1000;

/** How long a device rings for. */
export const RING_DURATION_S = 30;

export const LostModePayload = z.object({
  message: z.string().trim().min(1, 'Write a message for whoever finds it.').max(300),
  contact: z.string().trim().max(120).default(''),
});
export type LostModePayload = z.infer<typeof LostModePayload>;

/** POST /devices/:id/commands (dashboard). */
export const CreateCommandRequest = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ring') }),
  z.object({ type: z.literal('locate_now') }),
  z.object({ type: z.literal('lost_mode_on'), payload: LostModePayload }),
  z.object({ type: z.literal('lost_mode_off') }),
]);
export type CreateCommandRequest = z.input<typeof CreateCommandRequest>;

export const Command = z.object({
  id: Id,
  deviceId: Id,
  type: CommandType,
  payload: z.object({ message: z.string(), contact: z.string() }).nullable(),
  status: CommandStatus,
  createdAt: IsoDateTime,
  deliveredAt: IsoDateTime.nullable(),
  completedAt: IsoDateTime.nullable(),
  expiresAt: IsoDateTime.nullable(),
  error: z.string().nullable(),
});
export type Command = z.infer<typeof Command>;

export const CommandListResponse = z.object({ commands: z.array(Command) });

/** POST /devices/me/commands/:id/ack (agent). */
export const CommandAckRequest = z.object({
  status: z.enum(['done', 'failed']),
  error: z.string().max(300).optional(),
});
export type CommandAckRequest = z.input<typeof CommandAckRequest>;

export const CommandIdParams = z.object({ id: Id });
